import { Add } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useFormikContext } from "formik";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFormProfessionals } from "../../../../actions/forms";
import ConfirmDialog from "../../../commons/dialog/confirmDialog";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikDateTimePicker from "../../../commons/formik/formikDateTimePicker";
import useFormCalendar from "../../../commons/hooks/useFormCalendar";
import { StyledCommonForms } from "../../../styled/form";
import AddAppointment from "../../accident/accidentDashboard/appointmentsCalendar/addAppointment";

const AppointmentControl = ({
  addAppointmentControl,
  appointmentModel,
  artFields,
  name,
  handleAppointmentSubmit,
  openAppointment,
  handleOpenAppointment,
  handleCloseAppointment,
  especialidadesProfesional,
  required,
  disabled,
  idArt,
  preloadedProfessional,
  preloadedMnMd,
  preloadedSpecialty,
}) => {
  const dispatch = useDispatch();
  const { setFieldValue } = useFormikContext();
  const calendar = useFormCalendar();

  const professionals = useSelector((state) => state.forms.professionals);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [accidentAppointments, setAccidentAppointments] = useState(
    calendar.data.appointments,
  );
  const [errorProfessionalsDuplicated, setErrorProfessionalsDuplicated] =
    useState(false);

  const appointmentArtFields = {
    fechaHora: artFields?.[name],
    profesionalApellidoNombre: artFields?.profesionalApellidoNombre,
    matricula: artFields?.profesionalMnMp,
    especialidad:
      artFields?.especialidadInforme || artFields?.especialidadProfesional,
    observaciones: artFields?.observaciones,
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmCloseAppointment = () => {
    handleCloseAppointment();
    handleCloseConfirmDialog();
  };
  return (
    <StyledCommonForms.Container>
      {addAppointmentControl ? (
        <StyledCommonForms.AppointmentControl>
          <FormikAutocomplete
            name={name}
            label={`Fecha Hora Próxima Consulta ${required ? "*" : ""}`}
            variant="outlined"
            options={accidentAppointments}
            callBack={(value) => {
              if (value) {
                handleAppointmentSubmit(value);
              } else {
                handleAppointmentSubmit({
                  fechaHora: null,
                  especialidad: null,
                  profesional: null,
                  profesionalApellidoNombre: null,
                  matricula: "",
                  observaciones: "",
                  traslado: null,
                });
              }
            }}
            sx={{ ...StyledCommonForms.input }}
            returnProp="fechaHora"
            labelOption={(option) => option.fechaHora}
            formatLabelOption={(label) =>
              moment(label).format("DD/MM/YYYY HH:mm")
            }
            renderOption={(option) =>
              `${moment(option.fechaHora).format("DD/MM/YYYY HH:mm")} - ${option.profesionalApellidoNombre
              }`
            }
            filterOptions={(option) =>
              moment(option.fechaHora).format("DD/MM/YYYY HH:mm") +
              option.profesionalApellidoNombre
            }
            disabled={disabled}
          />
          <Tooltip
            title="Agregar Turno"
            onClick={disabled ? undefined : handleOpenAppointment}>
            <IconButton
              disabled={disabled}
              sx={
                disabled
                  ? { ...StyledCommonForms.addAppointmentDisabled }
                  : { ...StyledCommonForms.addAppointment }
              }>
              <Add />
            </IconButton>
          </Tooltip>
          <AddAppointment
            open={openAppointment}
            handleClose={handleCloseAppointment}
            handleOpenConfirmDialog={handleOpenConfirmDialog}
            artFields={appointmentArtFields}
            handleSubmit={(values, createProfessional) => {
              // Resetear error de profesionales duplicados
              setErrorProfessionalsDuplicated(false);

              // Manejar creación de un nuevo profesional
              if (createProfessional) {
                const isDuplicate = professionals.data.some(
                  (p) =>
                    p.nombre.toUpperCase() ===
                    values.profesionalApellidoNombre.toUpperCase(),
                );

                if (isDuplicate) {
                  setErrorProfessionalsDuplicated(true);
                  return;
                }

                const newProfessional = {
                  nombre: values.profesionalApellidoNombre,
                  especialidad: values.especialidad,
                  especialidades: [{ especialidad: values.especialidad }],
                  nombreEspecialidad: values.nombreEspecialidad,
                  matricula: values.matricula,
                  id: "",
                };

                dispatch(setFormProfessionals(newProfessional));
              }

              let existingAppointment = calendar.data.appointments?.find(
                (a) => a.fechaHora === values.fechaHora,
              );

              const appointmentAux = existingAppointment
                ? { ...existingAppointment, ...values }
                : { ...values };

              if (!existingAppointment) {
                delete appointmentAux.idTurnoSiniestro;
              }

              const updatedAppointments = [
                ...calendar.data.appointments?.filter(
                  (a) => a.fechaHora !== values.fechaHora,
                ),
                appointmentAux,
              ];
              setAccidentAppointments(updatedAppointments);
              setFieldValue(name, values.fechaHora);
              handleCloseAppointment();

              return handleAppointmentSubmit({
                ...appointmentAux,
                fromDB: false,
              });
            }}
            appointmentModel={appointmentModel}
            especialidadesProfesional={especialidadesProfesional.options}
            artId={idArt}
            errorProfessionalsDuplicated={errorProfessionalsDuplicated}
            openInCalendar={false}
            enablePastDates={false}
            preloadedProfessional={preloadedProfessional}
            preloadedMnMd={preloadedMnMd}
            preloadedSpecialty={preloadedSpecialty}
          />
        </StyledCommonForms.AppointmentControl>
      ) : (
        <FormikDateTimePicker
          name={name}
          label={`Fecha Hora Próxima Consulta ${required ? "*" : ""}`}
          inputVariant="outlined"
          disablePast
          sx={StyledCommonForms.input}
          disabled={disabled}
          autoComplete="off"
        />
      )}
      <div>
        <ConfirmDialog
          open={confirmDialogOpen}
          handleClose={handleCloseConfirmDialog}
          handleConfirm={handleConfirmCloseAppointment}
          title="CERRAR VENTANA"
          text="¿Está seguro que desea cancelar esta operación? En caso de aceptar, se perderá la información ingresada"
        />
      </div>
    </StyledCommonForms.Container>
  );
};

export default AppointmentControl;
